import React from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, Lock, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice, shopifyCheckoutUrl, SHOPIFY_DOMAIN, SHOPIFY_STOREFRONT_URL } from "@/lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
  const cart = useCart();
  if (!cart) return null;
  const { items, open, setOpen, updateQty, removeItem, count, total } = cart;

  const handleCheckout = () => {
    const url = shopifyCheckoutUrl(items);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      toast.info("Real checkout activates once the Shopify store is linked.", {
        description: SHOPIFY_DOMAIN
          ? "Some products still need their Shopify variant IDs."
          : "Your store domain + product variant IDs connect in one place — then this button opens Shopify's secure checkout with Apple Pay, Shop Pay, cards and supported crypto.",
      });
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="w-full sm:max-w-[460px] bg-[#0F0F14] border-l border-[#2A2A36] p-0 flex flex-col"
      >
        <div className="px-6 py-5 border-b border-[#1A1A22] flex items-center justify-between">
          <SheetTitle className="font-display font-extrabold uppercase text-xl text-white tracking-tight">
            Your Cart <span className="text-[#F26522]">({count})</span>
          </SheetTitle>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8 text-center">
            <p className="font-display font-bold uppercase text-2xl text-white">The cart is empty</p>
            <p className="text-zinc-300">The uniform of the movement is waiting in the shop.</p>
            <a
              href={SHOPIFY_STOREFRONT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cart-empty-shop-link"
              onClick={() => setOpen(false)}
              className="h-14 px-8 bg-[#F26522] hover:bg-[#FF7733] text-black font-display font-extrabold uppercase tracking-widest flex items-center gap-2"
            >
              Shop XRABI <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </a>
          </div>
        ) : (
          <>
            <ul data-testid="cart-items-list" className="flex-1 overflow-y-auto divide-y divide-[#1A1A22] px-6">
              {items.map((i) => (
                <li key={i.key} data-testid={`cart-item-${i.productId}`} className="py-5 flex gap-4">
                  <img src={i.image} alt="" loading="lazy" decoding="async" className="w-20 h-24 object-cover border border-[#2A2A36] shrink-0" />
                  <div className="flex-1 min-w-0 flex flex-col gap-2">
                    <p className="font-display font-bold uppercase text-sm text-white leading-tight">{i.name}</p>
                    <p className="font-mono text-xs tracking-[0.15em] uppercase text-zinc-300">
                      {i.size} • {i.color}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <button
                        data-testid={`cart-decrease-${i.productId}`}
                        onClick={() => updateQty(i.key, i.qty - 1)}
                        aria-label={`Decrease quantity of ${i.name}`}
                        className="w-11 h-11 border border-[#2A2A36] hover:border-[#F26522] text-white flex items-center justify-center"
                      >
                        <Minus className="w-4 h-4" aria-hidden="true" />
                      </button>
                      <span data-testid={`cart-qty-${i.productId}`} className="font-mono text-base text-white w-6 text-center">
                        {i.qty}
                      </span>
                      <button
                        data-testid={`cart-increase-${i.productId}`}
                        onClick={() => updateQty(i.key, i.qty + 1)}
                        aria-label={`Increase quantity of ${i.name}`}
                        className="w-11 h-11 border border-[#2A2A36] hover:border-[#F26522] text-white flex items-center justify-center"
                      >
                        <Plus className="w-4 h-4" aria-hidden="true" />
                      </button>
                      <button
                        data-testid={`cart-remove-${i.productId}`}
                        onClick={() => removeItem(i.key)}
                        aria-label={`Remove ${i.name} from cart`}
                        className="ml-auto w-11 h-11 text-zinc-300 hover:text-[#F26522] flex items-center justify-center"
                      >
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  <p className="font-mono text-sm text-[#F26522] font-semibold shrink-0">
                    {formatPrice(i.price * i.qty)}
                  </p>
                </li>
              ))}
            </ul>

            <div className="border-t border-[#1A1A22] px-6 py-5 flex flex-col gap-4 pb-[calc(20px+env(safe-area-inset-bottom))]">
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm tracking-[0.2em] uppercase text-zinc-300">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-mono text-xl font-semibold text-white">
                  {formatPrice(total)}
                </span>
              </div>
              <button
                data-testid="cart-checkout-button"
                onClick={handleCheckout}
                className="h-16 bg-[#F26522] hover:bg-[#FF7733] text-black font-display font-extrabold uppercase tracking-widest text-lg flex items-center justify-center gap-3 transition-all hover:-translate-y-0.5 active:scale-95"
              >
                <Lock className="w-5 h-5" aria-hidden="true" />
                Checkout via Shopify
              </button>
              <p className="text-center text-sm text-zinc-300">
                Secure Shopify checkout — Apple Pay, Shop Pay, cards &amp; supported crypto appear there when enabled.
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
